import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, inject } from "vue";
import dayjs from "dayjs";
import {
  BookStatusCode,
  BookHistoryDateCriteria,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  ShowErrorModal,
  BookStatusName,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  ShowToastMessage,
} from "@/types";
import { IBookInfo } from "@/domain/entities/book";
import { useBookStore } from "@/stores/bookStore";
import Page from "@/components/shared/Page.vue";
import NavigationBar from "@/components/shared/NavigationBar.vue";
import SegmentedControl from "@/components/shared/SegmentedControl.vue";
import BookHistoryDetail from "@/views/book/BookHistoryDetail.vue";
import FilterModal from "@/components/shared/modal/FilterModal.vue";
import { IResultData } from "@/domain/entities/shared/result";
import BookHistoryTable from "@/components/book/BookHistoryTable.vue";
import PageModal from "@/components/shared/modal/base/PageModal.vue";


const __default__ = {
  name: "BookHistory",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose: __expose }) {
  __expose();

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;

const bookStore = useBookStore();

const segmentTitles = ["다가오는 예약", "지난 예약"];
const currentSegmentIndex = ref(0);
const shouldShowFilters = ref(false);
const shouldShowFilterMenu = computed(() =>
  currentSegmentIndex.value === 0 ? false : true
);

const showFilterModal = () => {
  shouldShowFilters.value = true;
};

const FILTER_KEY_PERIOD = "조회기간";
const FILTER_KEY_TYPE = "필터";
const FILTER_KEY_SORT = "정렬기준";

const currentFilters = ref<Map<string, string>>(new Map());
currentFilters.value.set(FILTER_KEY_PERIOD, "1개월");
currentFilters.value.set(FILTER_KEY_TYPE, "전체");
currentFilters.value.set(FILTER_KEY_SORT, "오름차순");

const filters = new Map();
filters.set(FILTER_KEY_PERIOD, ["1개월", "3개월", "6개월", "전체"]);
filters.set(FILTER_KEY_TYPE, ["전체", "탑승", "미탑승", "취소"]);
filters.set(FILTER_KEY_SORT, ["오름차순", "내림차순"]);

const upcomingBookInfos = ref<IBookInfo[]>([]);
const pastBookInfos = ref<IBookInfo[]>([]);
const filteredPastBookInfos = ref<IBookInfo[]>([]);

const currentBookInfos = computed((): IBookInfo[] => {
  return currentSegmentIndex.value === 0
    ? upcomingBookInfos.value
    : filteredPastBookInfos.value;
});

// 스크롤 처리
const bookHistoryContentRef = ref<HTMLElement>();
const scrollToTop = () => {
  bookHistoryContentRef.value?.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

/** Swipe 처리 */
const onChangedSegmentIndex = (index: number) => {
  currentSegmentIndex.value = index;
  if (index === 0) {
    getUpcommingBookInfos();
  } else {
    getPastBookInfos();
  }
};

let touchStartX = 0;
let touchEndX = 0;
const segmentedControl = ref<InstanceType<typeof SegmentedControl>>();
const onTouchStart = (event: TouchEvent) => {
  touchStartX = event.changedTouches[0].screenX;
};
const onTouchEnd = (event: TouchEvent) => {
  touchEndX = event.changedTouches[0].screenX;
  if (Math.abs(touchEndX - touchStartX) < 100) {
    return;
  }
  currentSegmentIndex.value = touchEndX < touchStartX ? 1 : 0;
  segmentedControl.value?.changeSegmentIndex(currentSegmentIndex.value);
};

// 탑승내역 request 파라미터
const pastBookInfoRequest = computed(() => {
  let fromBookDate = "";
  let toBookDate = "";

  const period = currentFilters.value.get(FILTER_KEY_PERIOD) ?? "1";
  if (period !== "전체") {
    fromBookDate = dayjs()
      .add(-Number(period.replace(/[^0-9]/g, "")), "month")
      .format("YYYY-MM-DD");
    toBookDate = dayjs().format("YYYY-MM-DD");
  }

  const order =
    currentFilters.value.get(FILTER_KEY_SORT) === "내림차순" ? "desc" : "asc";
  const sort = `expectedOnDtm ${order}, bookTs ${order}`;
  const dateCriteria = BookHistoryDateCriteria.BOOK;

  return { fromBookDate, toBookDate, sort, dateCriteria };
});

// 예약내역 request 파라미터
const upcomingBookInfoRequest = ref({
  dateCriteria: BookHistoryDateCriteria.BOARD,
  fromBoardingDate: dayjs().format("YYYY-MM-DD"),
  sort: "expectedOnDtm asc",
});

// 예약 목록 오류 팝업
const showBookInfoErrorModal = (bookInfosResult: IResultData<IBookInfo[]>) => {
  showErrorModal(
    "예약목록 오류",
    bookInfosResult.errorMessage,
    bookInfosResult.resultCode,
    bookInfosResult.errorTime
  );
};

// 다가오는 예약 목록 가져오기 - 정렬(탑승일 기준 오늘 이후, 탑승 예정일 순)
const getUpcommingBookInfos = async () => {
  const bookInfosResult = await bookStore.getBookInfos(
    BookStatusCode.END,
    upcomingBookInfoRequest.value.sort,
    upcomingBookInfoRequest.value.dateCriteria,
    upcomingBookInfoRequest.value.fromBoardingDate
  );

  if (!bookInfosResult.isSuccess) {
    showBookInfoErrorModal(bookInfosResult);
  }
  upcomingBookInfos.value =
    bookInfosResult.data?.filter(
      (bookInfo) => bookInfo.bookStatus === BookStatusName.BOOK
    ) ?? [];
};

// 지난 예약 목록 가져오기 - 정렬(1개월전 ~ 오늘, 탑승 예정일 순 > 예약일시 순)
const getPastBookInfos = async () => {
  const bookInfosResult = await bookStore.getBookInfos(
    `${BookStatusCode.END}, ${BookStatusCode.CANCEL}`,
    pastBookInfoRequest.value.sort,
    pastBookInfoRequest.value.dateCriteria,
    pastBookInfoRequest.value.fromBookDate,
    pastBookInfoRequest.value.dateCriteria,
    pastBookInfoRequest.value.toBookDate
  );

  if (!bookInfosResult.isSuccess) {
    showBookInfoErrorModal(bookInfosResult);
  }

  pastBookInfos.value = bookInfosResult.data ?? [];
  const bookStatus = currentFilters.value.get(FILTER_KEY_TYPE);
  if (bookStatus === "전체") {
    filteredPastBookInfos.value = pastBookInfos.value.filter(
      (bookInfo) => bookInfo.bookStatus !== BookStatusName.BOOK
    );
    return;
  }
  filteredPastBookInfos.value = pastBookInfos.value.filter((bookInfo) => {
    return (
      bookInfo.bookStatus !== BookStatusName.BOOK &&
      bookInfo.bookStatus === bookStatus
    );
  });
};

// 지난예약 필터 처리
const getCurrentFilterString = () => {
  return Array.from(currentFilters.value.values()).join(" · ");
};
const onFilterChanged = async (filters: Map<string, string>) => {
  scrollToTop();
  shouldShowFilters.value = false;
  currentFilters.value = filters;
  getPastBookInfos();
};

// 예약 상세 화면 보여주기
const shouldShowBookHistoryDetail = ref(false);
const toggleShowBookHistoryDetail = (show: boolean) => {
  shouldShowBookHistoryDetail.value = show;
};

const selectedBookInfo = ref<IBookInfo>();
const showBookHistoryDetail = (bookInfo: IBookInfo) => {
  selectedBookInfo.value = bookInfo;
  toggleShowBookHistoryDetail(true);
};

// 예약 취소 처리
const updateKey = ref(0);
const onCompleteBookCancel = async () => {
  toggleShowBookHistoryDetail(false);
  onChangedSegmentIndex(1);
  updateKey.value += 1;
  showToastMessage("예약이 취소되었습니다");
};

const __returned__ = { showErrorModal, showToastMessage, bookStore, segmentTitles, currentSegmentIndex, shouldShowFilters, shouldShowFilterMenu, showFilterModal, FILTER_KEY_PERIOD, FILTER_KEY_TYPE, FILTER_KEY_SORT, currentFilters, filters, upcomingBookInfos, pastBookInfos, filteredPastBookInfos, currentBookInfos, bookHistoryContentRef, scrollToTop, onChangedSegmentIndex, get touchStartX() { return touchStartX }, set touchStartX(v) { touchStartX = v }, get touchEndX() { return touchEndX }, set touchEndX(v) { touchEndX = v }, segmentedControl, onTouchStart, onTouchEnd, pastBookInfoRequest, upcomingBookInfoRequest, showBookInfoErrorModal, getUpcommingBookInfos, getPastBookInfos, getCurrentFilterString, onFilterChanged, shouldShowBookHistoryDetail, toggleShowBookHistoryDetail, selectedBookInfo, showBookHistoryDetail, updateKey, onCompleteBookCancel, Page, NavigationBar, SegmentedControl, BookHistoryDetail, FilterModal, BookHistoryTable, PageModal }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})